import { useState, useEffect } from 'react';
import { Button, TextField, Icon, Avatar } from '@a-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { closeModal } from '../../../store/slices/modalReducer';
import { getDomainMembersForDomain, getWorkgroups, getDomains } from '../../../store/slices/domainsReducer';
import { startTaskBulk, fetchTask, fetchTasks, releaseTaskBulk, assignTaskBulk } from '../../../store/slices/tasksReducer';
import { getActiveExecutors } from '../../../helpers/task.helper';

const StartTaskBulk = () => {
  const dispatch = useDispatch();
  const { task } = useSelector(state => state.tasks);
  const { members, membersLoading, workgroups, workgroupsLoading, domains } = useSelector(state => state.domains);
  const { user } = useSelector(state => state.user);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [originalActiveExecutors, setOriginalActiveExecutors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Get domain members and workgroups when component mounts
  useEffect(() => {
    if (task?.domainId) {
      dispatch(getDomainMembersForDomain(task.domainId));
    } else {
      console.log('No domain ID found in task:', task);
    }
    
    // Fetch workgroups and domains
    dispatch(getWorkgroups());
    dispatch(getDomains());
  }, [task?.domainId, dispatch]);

  // Auto-select active executors or current user when members are loaded
  useEffect(() => {    
    if (members.length > 0 && selectedUsers.length === 0) {
      // Get active executors from task assignments
      const activeExecutors = getActiveExecutors(task?.taskAssignments);
      setOriginalActiveExecutors(activeExecutors);
      
      if (activeExecutors.length > 0) {
        // Select all active executors
        setSelectedUsers(activeExecutors);
      } else if (user?.userName) {
        // Fallback to current user if no active executors
        const currentUserMember = members.find(member => {
          return member.todoAppUser.maccount === user.userName.toUpperCase();
        });
        
        console.log('No active executors found, selecting current user:', currentUserMember);
        
        if (currentUserMember) {
          const currentUserMAccount = currentUserMember.todoAppUser.maccount;
          setSelectedUsers([currentUserMAccount]);
        } else {
          console.log('Current user not found in members list');
        }
      }
    }
  }, [members, user?.userName, selectedUsers.length, task?.taskAssignments]);

  // Helper function to get user initials
  const getUserInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  // Helper function to remove user from selected list
  const removeSelectedUser = (mAccount) => {
    setSelectedUsers(prev => prev.filter(user => user !== mAccount));
  };

  // Helper function to add user to selected list
  const addSelectedUser = (member) => {
    const mAccount = member.todoAppUser.maccount;
    if (!selectedUsers.includes(mAccount)) {
      setSelectedUsers(prev => [...prev, mAccount]);
    }
  };

  // Helper function to get workgroups for the current task's domain
  const getWorkgroupsForDomain = () => {
    if (!workgroups || !task?.domainId || !domains) {
     return [];
    }
    
    // Find the domain name from the domains array using the task's domainId
    const taskDomain = domains.find(domain => domain.id === task.domainId);
    if (!taskDomain) {
     return [];
    }
    
    const filteredWorkgroups = workgroups.filter(workgroup => workgroup.domainName === taskDomain.name);
   return filteredWorkgroups;
  };

  // Helper function to select all members from a workgroup
  const selectWorkgroupMembers = (workgroup) => {
    const workgroupMAccounts = workgroup.workgroupMembers.map(member => member.todoAppUser.maccount);
    // Merge with current selection instead of replacing it
    setSelectedUsers(prev => {
      const merged = new Set([...prev, ...workgroupMAccounts]);
      return Array.from(merged);
    });
  };

  // Filter and process members
  const allMembers = [...members]
    .filter(member => {
      const isExecutor = member.domainRole === 'executor';
      const matchesSearch = searchTerm === '' || 
        member.todoAppUser.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.todoAppUser.maccount.toLowerCase().includes(searchTerm.toLowerCase());
      return isExecutor && matchesSearch;
    })
    .sort((a, b) => a.todoAppUser.name.localeCompare(b.todoAppUser.name));

  // Get selected member objects for display
  const selectedMembers = allMembers.filter(member => 
    selectedUsers.includes(member.todoAppUser.maccount)
  );

  const handleStartBulk = async () => {
    if (selectedUsers.length === 0) {
      console.log('No users selected, returning early');
      return;
    }
    
    setLoading(true);
    try {
      // Check if active executors changed and release tasks for removed users
      const usersToRelease = originalActiveExecutors.filter(executor => 
        !selectedUsers.includes(executor)
      );
      
      if (usersToRelease.length > 0) {
       try {
          await dispatch(releaseTaskBulk({ taskId: task.id, mAccounts: usersToRelease }));
        } catch (releaseError) {
          console.error('Error releasing tasks for removed users:', releaseError);
          // Continue with starting tasks even if release fails
        }
      }
      
      const mAccounts = selectedUsers; // already mAccount strings
      
      // Start tasks for selected users first
      // Only assign users to task AFTER successful start (commit happens here)
      const result = await dispatch(startTaskBulk({ taskId: task.id, mAccounts }));
      
      // Only assign users if startTaskBulk was successful
      if (result.type === 'task/startTaskBulk/fulfilled') {
        try {
          await dispatch(assignTaskBulk({ taskId: task.id, mAccounts }));
        } catch (assignError) {
          console.error('Error assigning users to task:', assignError);
          // Log error but don't fail the whole operation since task is already started
        }
      }
      
      await dispatch(fetchTask(task.id));
      dispatch(closeModal());
      // Refresh the task list to update the current tab (e.g., remove from "niettoegekend" if assigned to colleagues)
      // Use setTimeout to ensure modal closes before refreshing the list
      setTimeout(() => {
        dispatch(fetchTasks());
      }, 100);
    } catch (error) {
      console.error('=== Error in handleStartBulk ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxHeight: '450px', overflowY: 'auto', padding: '25px' }}>
      <div style={{ flex: '1 1 0%', overflow: 'auto' }}>
        <div>
          <b>Geselecteerde medewerkers</b>
          {originalActiveExecutors.length > 0 && (
            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>
              <i>Actieve uitvoerders zijn automatisch geselecteerd</i>
            </div>
          )}
          <div style={{ minHeight: '10px' }}></div>

          <ul style={{ padding: '0px', listStyle: 'none' }}>
            {selectedMembers.map((member) => (
              <li key={member.todoAppUser.maccount} className="selectable-list-item" style={{ cursor: 'unset', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', border: '1px solid #007bff', borderRadius: '4px', marginBottom: '8px', listStyle: 'none' }}>
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                  <div className="a-avatar a-avatar--small" style={{ backgroundColor: 'rgb(176, 176, 176)', marginRight: '8px' }}>
                    <span className="a-avatar__letter" style={{ color: 'white' }}>{getUserInitials(member.todoAppUser.name)}</span>
                    <span className="u-screen-reader-only">Gebruiker icoon</span>
                  </div>
                  <div>
                    <div><b>{member.todoAppUser.name}</b></div>
                    <div><i className="domain"><span>{member.todoAppUser.maccount}</span></i></div>
                  </div>
                </div>
                <button 
                  className="a-button a-button--transparent a-button--small has-icon" 
                  title="" 
                  aria-label="" 
                  style={{ color: 'red', backgroundColor: 'white', border: 'none', boxShadow: 'none' }}
                  onMouseEnter={(e) => e.target.style.boxShadow = 'none'}
                  onMouseLeave={(e) => e.target.style.boxShadow = 'none'}
                  onClick={() => removeSelectedUser(member.todoAppUser.maccount)}
                >
                  <span className="ai-bin ai">
                    <svg aria-hidden="true">
                      <use href="#ai-bin"></use>
                    </svg>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div style={{ minHeight: '10px' }}></div>

        {/* Ploegen Shortcut Buttons */}
        <div>
          <b>Ploegen</b>
          <div style={{ minHeight: '10px' }}></div>
          {workgroupsLoading === 'loading' ? (
            <div style={{ color: '#666', fontStyle: 'italic' }}>Ploegen laden...</div>
          ) : workgroupsLoading === 'error' ? (
            <div style={{ color: '#d32f2f', fontStyle: 'italic' }}>Fout bij laden ploegen</div>
          ) : getWorkgroupsForDomain().length > 0 ? (
            <ul style={{ padding: '0px', listStyle: 'none' }}>
              {getWorkgroupsForDomain().map((workgroup) => (
                <li 
                  key={workgroup.id} 
                  className="selectable-list-item"
                  onClick={() => selectWorkgroupMembers(workgroup)}
                  style={{ 
                    border: '1px solid #007bff', 
                    borderRadius: '4px', 
                    marginBottom: '8px', 
                    padding: '8px', 
                    cursor: 'pointer', 
                    listStyle: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                    <div className="a-avatar a-avatar--small" style={{ backgroundColor: 'rgb(0, 123, 255)', marginRight: '8px' }}>
                      <span className="a-avatar__letter" style={{ color: 'white' }}>{getUserInitials(workgroup.name)}</span>
                      <span className="u-screen-reader-only">Ploeg icoon</span>
                    </div>
                    <div>
                      <div><b>{workgroup.name}</b></div>
                      <div><i className="domain"><span>{workgroup.workgroupMembers.length} leden</span></i></div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '16px' }}>Geen ploegen beschikbaar voor dit domein</div>
          )}
        </div>

        <div style={{ minHeight: '10px' }}></div>

        <div style={{ cursor: 'pointer' }}>
          <h5 style={{ color: 'rgb(0, 87, 183)' }}>
            Filter
            <span className="ai-arrow-down-1 ai" style={{ float: 'right' }}>
              <svg aria-hidden="true">
                <use href="#ai-arrow-down-1"></use>
              </svg>
            </span>
          </h5>
        </div>

        <hr />
        <div style={{ minHeight: '10px' }}></div>

        <div className="a-input has-icon-right">
          <div className="a-input__wrapper">
            <input 
              type="text" 
              placeholder="Medewerker zoeken" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="ai-search ai">
              <svg aria-hidden="true">
                <use href="#ai-search"></use>
              </svg>
            </span>
          </div>
        </div>

        <div style={{ minHeight: '10px' }}></div>

        <ul className="m-selectable-list m-selectable-list--no-border" style={{ padding: '0px', listStyle: 'none' }}>
          {allMembers.map((member) => {
            const isSelected = selectedUsers.includes(member.todoAppUser.maccount);
            return (
              <li 
                key={member.todoAppUser.maccount} 
                className="selectable-list-item"
                onClick={() => {
                  if (!isSelected) {
                    addSelectedUser(member);
                  }
                }}
                style={{ border: '1px solid #007bff', borderRadius: '4px', marginBottom: '8px', padding: '8px', cursor: 'pointer', listStyle: 'none', display: 'flex', alignItems: 'center' }}
              >
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                  <div className="a-avatar a-avatar--small" style={{ backgroundColor: 'rgb(176, 176, 176)', marginRight: '8px' }}>
                    <span className="a-avatar__letter" style={{ color: 'white' }}>{getUserInitials(member.todoAppUser.name)}</span>
                    <span className="u-screen-reader-only">Gebruiker icoon</span>
                  </div>
                  <div>
                    <div><b>{member.todoAppUser.name}</b></div>
                    <div><i className="domain"><span>{member.todoAppUser.maccount}</span></i></div>
                  </div>
                </div>
              </li>
            );
          })}
          {allMembers.length === 0 && searchTerm && (
            <li className="selectable-list-item">
              <div className="u-text-center u-text-muted u-padding">
                Geen medewerkers gevonden voor "{searchTerm}"
              </div>
            </li>
          )}
        </ul>

        {/* Action Buttons */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <Button 
            onClick={handleStartBulk}
            disabled={selectedUsers.length === 0 || loading || !task?.domainId}
          >
            {loading && <Icon className="u-margin-right-xs" name="ai-spinner"/>}
            Start tijdregistratie voor {selectedUsers.length} gebruiker{selectedUsers.length !== 1 ? 's' : ''}
          </Button>
          <Button
            emphasis="medium"
            onClick={() => dispatch(closeModal())}
            disabled={loading}
          >
            Annuleren
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StartTaskBulk;
